import React, { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Upload, X, Star, Loader2, ImagePlus, Image as ImageIcon } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

/**
 * Property photo gallery modal.
 * - Upload multiple photos
 * - Delete photos
 * - Set any photo as cover (first position)
 */
export const PropertyGalleryModal = ({ open, onClose, property, onChange }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(null); // url being processed

  if (!property) return null;

  const images = property.images || [];
  const resolveUrl = (u) => (u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      let latest = images;
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        const res = await api.post(`/properties/${property.id}/photos`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        latest = res.data.images;
      }
      toast.success(`${files.length} photo(s) ajoutee(s)`);
      onChange?.({ ...property, images: latest });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (url) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    setBusy(url);
    try {
      const res = await api.delete(`/properties/${property.id}/photos`, { params: { url } });
      onChange?.({ ...property, images: res.data.images });
      toast.success('Photo supprimee');
    } catch {
      toast.error('Erreur');
    } finally {
      setBusy(null);
    }
  };

  const handleSetCover = async (url) => {
    setBusy(url);
    try {
      const res = await api.post(`/properties/${property.id}/photos/cover`, null, { params: { url } });
      onChange?.({ ...property, images: res.data.images });
      toast.success('Photo de couverture mise a jour');
    } catch {
      toast.error('Erreur');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[900px] max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1B3A4B]">
            <ImageIcon className="w-5 h-5" />
            Galerie photos - {property.title}
          </DialogTitle>
          <DialogDescription>
            {images.length} photo{images.length > 1 ? 's' : ''} • La premiere est la couverture
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 px-1 py-2">
          {/* Upload zone */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#1B3A4B] transition-colors mb-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={handleUpload}
              data-testid="gallery-upload-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-[#1B3A4B] hover:bg-[#142D3A]"
              data-testid="gallery-upload-btn"
            >
              {uploading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Upload en cours...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> Ajouter des photos</>
              )}
            </Button>
            <p className="text-xs text-gray-500 mt-2">JPG / PNG / WEBP • Upload multiple possible</p>
          </div>

          {/* Grid */}
          {images.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <ImagePlus className="w-16 h-16 mx-auto mb-3 opacity-40" />
              <p>Aucune photo pour le moment</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {images.map((url, idx) => (
                <div
                  key={url}
                  className="relative group rounded-lg overflow-hidden border-2 border-transparent hover:border-[#1B3A4B] bg-gray-100 aspect-[4/3]"
                  data-testid={`gallery-photo-${idx}`}
                >
                  <img src={resolveUrl(url)} alt={`photo ${idx + 1}`} className="w-full h-full object-cover" />
                  {idx === 0 && (
                    <Badge className="absolute top-2 left-2 bg-[#D4A745] text-[#1B3A4B] border-0 gap-1">
                      <Star className="w-3 h-3 fill-current" /> Couverture
                    </Badge>
                  )}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    {idx !== 0 && (
                      <Button
                        size="sm"
                        onClick={() => handleSetCover(url)}
                        disabled={busy === url}
                        className="bg-[#D4A745] hover:bg-[#b8902f] text-[#1B3A4B]"
                        data-testid={`set-cover-${idx}`}
                      >
                        {busy === url ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Star className="w-3.5 h-3.5 mr-1" /> Couverture</>}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(url)}
                      disabled={busy === url}
                      data-testid={`delete-photo-${idx}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pt-3 border-t flex justify-end flex-shrink-0">
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
